import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Switch,
  FormControlLabel,
  TextField,
  MenuItem,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Collapse,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import PersonIcon from "@mui/icons-material/Person";
import GroupIcon from "@mui/icons-material/Group";
import CategoryIcon from "@mui/icons-material/Category";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import api from "../../utils/api";

// ─── Shared styles ─────────────────────────────────────────────────────────────

const cardSx = {
  borderRadius: "16px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
  backgroundColor: "rgba(255,255,255,0.98)",
  overflow: "hidden",
};

const dialogPaperSx = {
  borderRadius: "18px",
  background: "rgba(255,255,255,0.96)",
  backdropFilter: "blur(10px)",
  boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
  overflow: "hidden",
};

const fieldSx = {
  mt: 1.5,
  "& .MuiOutlinedInput-root": { backgroundColor: "#fff", borderRadius: "10px" },
};

const primaryBtnSx = {
  backgroundColor: "#2f63ad",
  color: "#fff",
  fontWeight: 700,
  borderRadius: "10px",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { backgroundColor: "#1f4f94", boxShadow: "none" },
};

const secondaryBtnSx = {
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 600,
};

// ─── FlagCell. inline switch that fires an immediate save ─────────────────────

const FlagCell = ({ checked, onChange, label, disabled }) => (
  <TableCell align="center" sx={{ px: 1 }}>
    <Tooltip title={label}>
      <Switch
        size="small"
        checked={Boolean(checked)}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        color="primary"
      />
    </Tooltip>
  </TableCell>
);

// ─── SectionHeader ─────────────────────────────────────────────────────────────

const SectionHeader = ({ icon, title, subtitle, action, open, onToggle }) => (
  <Box
    onClick={onToggle}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      px: 2.5,
      py: 2,
      cursor: onToggle ? "pointer" : "default",
      backgroundColor: "rgba(47,99,173,0.03)",
      borderBottom: "1px solid rgba(47,99,173,0.07)",
      userSelect: "none",
    }}
  >
    <Box sx={{ color: "#2f63ad", display: "flex", alignItems: "center" }}>{icon}</Box>
    <Box sx={{ flex: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142", lineHeight: 1.3 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" sx={{ color: "#6b778c" }}>
          {subtitle}
        </Typography>
      )}
    </Box>
    {action}
    {onToggle && (open ? <ExpandLessIcon sx={{ color: "#6b778c" }} /> : <ExpandMoreIcon sx={{ color: "#6b778c" }} />)}
  </Box>
);

// ─── Level1Controls. master switches ─────────────────────────────────────────

const Level1Controls = ({ moduleId, itemLabel, onSnack }) => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get(`/notification-settings/${moduleId}`)
      .then((res) =>
        setSettings({
          is_active: Boolean(res.data.is_active ?? 1),
          member_on_create: Boolean(res.data.member_on_create ?? 0),
          member_on_status_change: Boolean(res.data.member_on_status_change ?? 1),
          member_on_comment: Boolean(res.data.member_on_comment ?? 0),
        })
      )
      .catch(() => onSnack("Failed to load module settings", "error"));
  }, [moduleId, onSnack]);

  const save = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    setSaving(true);
    try {
      await api.put(`/notification-settings/${moduleId}`, next);
      onSnack("Settings saved");
    } catch {
      onSnack("Failed to save settings", "error");
      setSettings(settings); // rollback
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2.5, py: 2 }}>
      {/* Master kill-switch */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          borderRadius: "12px",
          backgroundColor: settings.is_active
            ? "rgba(22,163,74,0.05)"
            : "rgba(239,68,68,0.05)",
          border: `1px solid ${settings.is_active ? "rgba(22,163,74,0.2)" : "rgba(239,68,68,0.2)"}`,
          mb: 2.5,
        }}
      >
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#223142" }}>
            All {itemLabel} emails
          </Typography>
          <Typography variant="caption" sx={{ color: "#6b778c" }}>
            {settings.is_active
              ? "Emails are enabled for this module"
              : "All emails are paused for this module"}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {saving && <CircularProgress size={16} />}
          <Chip
            label={settings.is_active ? "Active" : "Paused"}
            size="small"
            color={settings.is_active ? "success" : "error"}
            sx={{ fontWeight: 700 }}
          />
          <Switch
            checked={settings.is_active}
            onChange={(e) => save({ is_active: e.target.checked })}
            color="success"
          />
        </Box>
      </Box>

      {/* Member notification flags */}
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 700,
          color: "#223142",
          mb: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 0.75,
        }}
      >
        <PersonIcon sx={{ fontSize: 18, color: "#2f63ad" }} />
        Member (property owner) auto-notifications
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          pl: 1,
          borderLeft: "3px solid rgba(47,99,173,0.15)",
        }}
      >
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={settings.member_on_create}
              onChange={(e) => save({ member_on_create: e.target.checked })}
              disabled={!settings.is_active}
            />
          }
          label={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#223142" }}>
                On creation
              </Typography>
              <Typography variant="caption" sx={{ color: "#6b778c" }}>
                Email member when a {itemLabel} is created for their property
              </Typography>
            </Box>
          }
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={settings.member_on_status_change}
              onChange={(e) => save({ member_on_status_change: e.target.checked })}
              disabled={!settings.is_active}
            />
          }
          label={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#223142" }}>
                On status change
              </Typography>
              <Typography variant="caption" sx={{ color: "#6b778c" }}>
                Email member when the {itemLabel} status is updated (also respects Status Triggers)
              </Typography>
            </Box>
          }
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={settings.member_on_comment}
              onChange={(e) => save({ member_on_comment: e.target.checked })}
              disabled={!settings.is_active}
            />
          }
          label={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#223142" }}>
                On new comment
              </Typography>
              <Typography variant="caption" sx={{ color: "#6b778c" }}>
                Email member when a staff comment is added to their {itemLabel}
              </Typography>
            </Box>
          }
        />
      </Box>
    </Box>
  );
};

// ─── RecipientDialog. add / edit Level 2 recipient ───────────────────────────

const BLANK_RECIPIENT = {
  email: "",
  on_create: true,
  on_status_change: true,
  on_comment: true,
  is_active: true,
};

const RecipientDialog = ({ open, editing, moduleId, onClose, onSaved }) => {
  const [form, setForm] = useState(BLANK_RECIPIENT);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setForm(
        editing
          ? {
              email: editing.email || "",
              on_create: Boolean(editing.on_create ?? 1),
              on_status_change: Boolean(editing.on_status_change ?? 1),
              on_comment: Boolean(editing.on_comment ?? 1),
              is_active: Boolean(editing.is_active ?? 1),
            }
          : BLANK_RECIPIENT
      );
      setErr("");
    }
  }, [open, editing]);

  const handleSave = async () => {
    if (!form.email.trim() || !form.email.includes("@")) {
      setErr("Please enter a valid email address.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, email: form.email.trim(), module_id: moduleId };
      if (editing?.id) {
        await api.put(`/emailNotification/${editing.id}`, payload);
      } else {
        await api.post("/emailNotification", payload);
      }
      onSaved();
      onClose();
    } catch {
      setErr("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const toggle = (field) => setForm((p) => ({ ...p, [field]: !p[field] }));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: dialogPaperSx }}>
      <DialogTitle sx={{ textAlign: "center", fontWeight: 700, fontSize: "1.25rem", pt: 3, pb: 1 }}>
        {editing ? "Edit Recipient" : "Add Recipient"}
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 1 }}>
        <TextField
          fullWidth
          label="Email Address"
          value={form.email}
          onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
          error={Boolean(err)}
          helperText={err}
          sx={fieldSx}
        />

        <Box
          sx={{
            mt: 2.5,
            p: 2,
            borderRadius: "12px",
            backgroundColor: "rgba(47,99,173,0.04)",
            border: "1px solid rgba(47,99,173,0.1)",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#223142", mb: 1.5 }}>
            Notify on…
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <FormControlLabel
              control={<Switch size="small" checked={form.on_create} onChange={() => toggle("on_create")} />}
              label="Record creation"
            />
            <FormControlLabel
              control={<Switch size="small" checked={form.on_status_change} onChange={() => toggle("on_status_change")} />}
              label="Status changes"
            />
            <FormControlLabel
              control={<Switch size="small" checked={form.on_comment} onChange={() => toggle("on_comment")} />}
              label="New comments"
            />
          </Box>
        </Box>

        <Box sx={{ mt: 1.5 }}>
          <FormControlLabel
            control={
              <Switch size="small" checked={form.is_active} onChange={() => toggle("is_active")} />
            }
            label="Active"
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1.5, justifyContent: "center", gap: 1.25 }}>
        <Button onClick={onClose} variant="outlined" sx={secondaryBtnSx} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" sx={primaryBtnSx} disabled={saving}>
          {saving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : editing ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Level2Recipients ─────────────────────────────────────────────────────────

const Level2Recipients = ({ moduleId, active, onSnack }) => {
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchRecipients = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/emailNotification", { params: { module_id: moduleId } });
      setRecipients(res.data || []);
    } catch {
      onSnack("Failed to load recipients", "error");
    } finally {
      setLoading(false);
    }
  }, [moduleId, onSnack]);

  useEffect(() => { fetchRecipients(); }, [fetchRecipients]);

  const updateFlag = async (recipient, field, value) => {
    const payload = { ...recipient, [field]: value, module_id: moduleId };
    // Optimistic update
    setRecipients((prev) =>
      prev.map((r) => (r.id === recipient.id ? { ...r, [field]: value } : r))
    );
    try {
      await api.put(`/emailNotification/${recipient.id}`, payload);
    } catch {
      onSnack("Failed to update", "error");
      fetchRecipients(); // rollback
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this recipient?")) return;
    try {
      await api.delete(`/emailNotification/${id}`);
      onSnack("Recipient removed");
      fetchRecipients();
    } catch {
      onSnack("Failed to remove recipient", "error");
    }
  };

  return (
    <Box sx={{ px: 2.5, py: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
        <Typography variant="body2" sx={{ color: "#6b778c" }}>
          These emails receive notifications based on their event subscriptions below.
        </Typography>
        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => { setEditing(null); setDialogOpen(true); }}
          disabled={!active}
          sx={{ ...primaryBtnSx, height: 34 }}
        >
          Add Email
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Paper elevation={0} sx={{ ...cardSx, border: "1px solid rgba(47,99,173,0.07)" }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "rgba(47,99,173,0.03)" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    <Tooltip title="Notify on record creation"><span>Create</span></Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    <Tooltip title="Notify on status changes"><span>Status</span></Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    <Tooltip title="Notify on new comments"><span>Comment</span></Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Active</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recipients.length > 0 ? (
                  recipients.map((r) => (
                    <TableRow key={r.id} sx={{ "&:last-child td": { border: 0 } }}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: "#223142" }}>
                          {r.email}
                        </Typography>
                      </TableCell>
                      <FlagCell
                        checked={r.on_create}
                        onChange={(v) => updateFlag(r, "on_create", v)}
                        label="Notify on creation"
                        disabled={!active || !r.is_active}
                      />
                      <FlagCell
                        checked={r.on_status_change}
                        onChange={(v) => updateFlag(r, "on_status_change", v)}
                        label="Notify on status change"
                        disabled={!active || !r.is_active}
                      />
                      <FlagCell
                        checked={r.on_comment}
                        onChange={(v) => updateFlag(r, "on_comment", v)}
                        label="Notify on comments"
                        disabled={!active || !r.is_active}
                      />
                      <FlagCell
                        checked={r.is_active}
                        onChange={(v) => updateFlag(r, "is_active", v)}
                        label="Active"
                      />
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => { setEditing(r); setDialogOpen(true); }}
                          sx={{ color: "#2f63ad" }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(r.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 3, textAlign: "center", color: "#9aa5b4" }}>
                      No global recipients yet. Add one above.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <RecipientDialog
        open={dialogOpen}
        editing={editing}
        moduleId={moduleId}
        onClose={() => setDialogOpen(false)}
        onSaved={() => { fetchRecipients(); onSnack(editing ? "Recipient updated" : "Recipient added"); }}
      />
    </Box>
  );
};

// ─── TypeRuleDialog. add / edit Level 3 rule ────────────────────────────────

const BLANK_RULE = {
  type_id: "",
  subtype_id: "",
  email: "",
  on_create: true,
  on_status_change: true,
  on_comment: true,
  is_active: true,
};

const TypeRuleDialog = ({
  open,
  editing,
  moduleId,
  types,
  subtypes,
  typeLabelKey,
  subtypeLabelKey,
  subtypeParentKey,
  onClose,
  onSaved,
}) => {
  const [form, setForm] = useState(BLANK_RULE);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setForm(
        editing
          ? {
              type_id: editing.type_id ?? "",
              subtype_id: editing.subtype_id ?? "",
              email: editing.email || "",
              on_create: Boolean(editing.on_create ?? 1),
              on_status_change: Boolean(editing.on_status_change ?? 1),
              on_comment: Boolean(editing.on_comment ?? 1),
              is_active: Boolean(editing.is_active ?? 1),
            }
          : BLANK_RULE
      );
      setErr("");
    }
  }, [open, editing]);

  const availableSubtypes = useMemo(() => {
    if (!form.type_id || !subtypes?.length) return [];
    return subtypes.filter((s) => Number(s[subtypeParentKey]) === Number(form.type_id));
  }, [form.type_id, subtypes, subtypeParentKey]);

  const handleSave = async () => {
    if (!form.email.trim() || !form.email.includes("@")) {
      setErr("Please enter a valid email address.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        module_id: moduleId,
        type_id: form.type_id ? Number(form.type_id) : null,
        subtype_id: form.subtype_id ? Number(form.subtype_id) : null,
        email: form.email.trim(),
        on_create: Boolean(form.on_create),
        on_status_change: Boolean(form.on_status_change),
        on_comment: Boolean(form.on_comment),
        is_active: Boolean(form.is_active),
      };
      if (editing?.id) {
        await api.put(`/notification-type-rules/${editing.id}`, payload);
      } else {
        await api.post("/notification-type-rules", payload);
      }
      onSaved();
      onClose();
    } catch {
      setErr("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const toggle = (field) => setForm((p) => ({ ...p, [field]: !p[field] }));

  const getTypeLabel = (id) => {
    if (!id) return "All Types";
    return types.find((t) => Number(t.id) === Number(id))?.[typeLabelKey] || `#${id}`;
  };

  const getSubtypeLabel = (id) => {
    if (!id) return "All Subtypes";
    return subtypes?.find((s) => Number(s.id) === Number(id))?.[subtypeLabelKey] || `#${id}`;
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: dialogPaperSx }}>
      <DialogTitle sx={{ textAlign: "center", fontWeight: 700, fontSize: "1.25rem", pt: 3, pb: 1 }}>
        {editing ? "Edit Type Rule" : "Add Type Rule"}
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 1 }}>
        <Typography sx={{ textAlign: "center", color: "#666", fontSize: "0.9rem", mb: 1 }}>
          Route notifications to an extra email based on type or subtype.
        </Typography>

        {/* Type */}
        <TextField
          select fullWidth label="Type (optional. leave blank for all)"
          value={form.type_id} sx={fieldSx}
          onChange={(e) => setForm((p) => ({ ...p, type_id: e.target.value, subtype_id: "" }))}
        >
          <MenuItem value=""><em>All Types (catch-all)</em></MenuItem>
          {types.map((t) => (
            <MenuItem key={t.id} value={t.id}>{t[typeLabelKey]}</MenuItem>
          ))}
        </TextField>

        {/* Subtype. only show if module supports it and type is selected */}
        {subtypes && subtypes.length > 0 && (
          <TextField
            select fullWidth label="Subtype (optional)"
            value={form.subtype_id} sx={fieldSx}
            disabled={!form.type_id}
          onChange={(e) => setForm((p) => ({ ...p, subtype_id: e.target.value }))}
          >
            <MenuItem value=""><em>All Subtypes</em></MenuItem>
            {availableSubtypes.map((s) => (
              <MenuItem key={s.id} value={s.id}>{s[subtypeLabelKey]}</MenuItem>
            ))}
          </TextField>
        )}

        {/* Email */}
        <TextField
          fullWidth label="Email Address"
          value={form.email} error={Boolean(err)} helperText={err}
          onChange={(e) => { setForm((p) => ({ ...p, email: e.target.value })); setErr(""); }}
          sx={fieldSx}
        />

        {/* Preview */}
        <Box
          sx={{
            mt: 2,
            px: 1.5,
            py: 1,
            borderRadius: "10px",
            backgroundColor: "rgba(47,99,173,0.04)",
            border: "1px solid rgba(47,99,173,0.08)",
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Chip label={getTypeLabel(form.type_id)} size="small" sx={{ fontWeight: 600 }} />
          {subtypes && <Chip label={getSubtypeLabel(form.subtype_id)} size="small" sx={{ fontWeight: 600 }} />}
          {form.email && <Chip icon={<EditIcon sx={{ fontSize: "14px !important" }} />} label={form.email} size="small" />}
        </Box>

        {/* Event flags */}
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: "12px",
            backgroundColor: "rgba(47,99,173,0.04)",
            border: "1px solid rgba(47,99,173,0.1)",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#223142", mb: 1 }}>
            Notify on…
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <FormControlLabel
              control={<Switch size="small" checked={form.on_create} onChange={() => toggle("on_create")} />}
              label="Record creation"
            />
            <FormControlLabel
              control={<Switch size="small" checked={form.on_status_change} onChange={() => toggle("on_status_change")} />}
              label="Status changes"
            />
            <FormControlLabel
              control={<Switch size="small" checked={form.on_comment} onChange={() => toggle("on_comment")} />}
              label="New comments"
            />
          </Box>
        </Box>

        <Box sx={{ mt: 1.5 }}>
          <FormControlLabel
            control={<Switch size="small" checked={form.is_active} onChange={() => toggle("is_active")} />}
            label="Active"
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1.5, justifyContent: "center", gap: 1.25 }}>
        <Button onClick={onClose} variant="outlined" sx={secondaryBtnSx} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" sx={primaryBtnSx} disabled={saving}>
          {saving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : editing ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Level3TypeRules ──────────────────────────────────────────────────────────

const Level3TypeRules = ({
  moduleId,
  active,
  types,
  subtypes,
  typeLabelKey,
  subtypeLabelKey,
  subtypeParentKey,
  onSnack,
}) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/notification-type-rules", { params: { module_id: moduleId } });
      setRules(res.data || []);
    } catch {
      onSnack("Failed to load type rules", "error");
    } finally {
      setLoading(false);
    }
  }, [moduleId, onSnack]);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  const updateFlag = async (rule, field, value) => {
    const payload = { ...rule, module_id: moduleId, [field]: value };
    setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, [field]: value } : r)));
    try {
      await api.put(`/notification-type-rules/${rule.id}`, payload);
    } catch {
      onSnack("Failed to update", "error");
      fetchRules();
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this rule?")) return;
    try {
      await api.delete(`/notification-type-rules/${id}`);
      onSnack("Rule removed");
      fetchRules();
    } catch {
      onSnack("Failed to remove rule", "error");
    }
  };

  const getLabel = (rule) => {
    const typeLabel = rule.type_id
      ? types.find((t) => Number(t.id) === Number(rule.type_id))?.[typeLabelKey] || `#${rule.type_id}`
      : "All Types";
    if (!subtypes) return typeLabel;
    const subLabel = rule.subtype_id
      ? subtypes.find((s) => Number(s.id) === Number(rule.subtype_id))?.[subtypeLabelKey] || `#${rule.subtype_id}`
      : "All Subtypes";
    return { typeLabel, subLabel };
  };

  return (
    <Box sx={{ px: 2.5, py: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
        <Typography variant="body2" sx={{ color: "#6b778c" }}>
          Route extra emails to specific addresses based on type or subtype.
        </Typography>
        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => { setEditing(null); setDialogOpen(true); }}
          disabled={!active}
          sx={{ ...primaryBtnSx, height: 34 }}
        >
          Add Rule
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Paper elevation={0} sx={{ ...cardSx, border: "1px solid rgba(47,99,173,0.07)" }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "rgba(47,99,173,0.03)" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  {subtypes !== null && <TableCell sx={{ fontWeight: 700 }}>Subtype</TableCell>}
                  <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    <Tooltip title="On creation"><span>Create</span></Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    <Tooltip title="On status change"><span>Status</span></Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    <Tooltip title="On comment"><span>Comment</span></Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Active</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.length > 0 ? (
                  rules.map((rule) => {
                    const labels = getLabel(rule);
                    return (
                      <TableRow key={rule.id} sx={{ "&:last-child td": { border: 0 } }}>
                        <TableCell>
                          <Chip
                            label={typeof labels === "string" ? labels : labels.typeLabel}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.75rem",
                              backgroundColor: rule.type_id ? "rgba(47,99,173,0.08)" : "rgba(234,179,8,0.1)",
                              color: rule.type_id ? "#2f63ad" : "#b45309",
                            }}
                          />
                        </TableCell>
                        {subtypes !== null && (
                          <TableCell>
                            <Typography variant="body2" sx={{ color: "#6b778c", fontSize: "0.8rem" }}>
                              {typeof labels === "object" ? labels.subLabel : "N/A"}
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: "#223142" }}>
                            {rule.email}
                          </Typography>
                        </TableCell>
                        <FlagCell
                          checked={rule.on_create}
                          onChange={(v) => updateFlag(rule, "on_create", v)}
                          label="On creation"
                          disabled={!active || !rule.is_active}
                        />
                        <FlagCell
                          checked={rule.on_status_change}
                          onChange={(v) => updateFlag(rule, "on_status_change", v)}
                          label="On status change"
                          disabled={!active || !rule.is_active}
                        />
                        <FlagCell
                          checked={rule.on_comment}
                          onChange={(v) => updateFlag(rule, "on_comment", v)}
                          label="On comment"
                          disabled={!active || !rule.is_active}
                        />
                        <FlagCell
                          checked={rule.is_active}
                          onChange={(v) => updateFlag(rule, "is_active", v)}
                          label="Active"
                        />
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => { setEditing(rule); setDialogOpen(true); }}
                            sx={{ color: "#2f63ad" }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDelete(rule.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={subtypes !== null ? 8 : 7}
                      sx={{ py: 3, textAlign: "center", color: "#9aa5b4" }}
                    >
                      No type-specific rules yet. Add one above to route emails by type or subtype.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <TypeRuleDialog
        open={dialogOpen}
        editing={editing}
        moduleId={moduleId}
        types={types}
        subtypes={subtypes}
        typeLabelKey={typeLabelKey}
        subtypeLabelKey={subtypeLabelKey}
        subtypeParentKey={subtypeParentKey}
        onClose={() => setDialogOpen(false)}
        onSaved={() => { fetchRules(); onSnack(editing ? "Rule updated" : "Rule added"); }}
      />
    </Box>
  );
};

// ─── Main: NotificationModuleSettings ─────────────────────────────────────────

const NotificationModuleSettings = ({
  moduleId,
  moduleName,
  itemLabel,
  typeEndpoint,
  subtypeEndpoint = null,
  typeLabelKey = "type",
  subtypeLabelKey = "subtype_name",
  subtypeParentKey = "type_id",
}) => {
  const [types, setTypes] = useState([]);
  const [subtypes, setSubtypes] = useState(null); // null = module has no subtypes
  const [moduleActive, setModuleActive] = useState(true);
  const [openSections, setOpenSections] = useState({ l1: true, l2: true, l3: true });
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const onSnack = useCallback((message, severity = "success") => {
    setSnack({ open: true, message, severity });
    if (severity === "success" && message.toLowerCase().includes("active")) {
      // Refresh moduleActive state if needed - handled by Level1Controls itself
    }
  }, []);

  useEffect(() => {
    // Fetch types
    api.get(typeEndpoint)
      .then((r) => setTypes(r.data || []))
      .catch(() => {});

    // Fetch subtypes (optional)
    if (subtypeEndpoint) {
      api.get(subtypeEndpoint)
        .then((r) => setSubtypes(r.data || []))
        .catch(() => setSubtypes([]));
    }

    // Read module active state for disabling child sections
    api.get(`/notification-settings/${moduleId}`)
      .then((r) => setModuleActive(Boolean(r.data.is_active ?? 1)))
      .catch(() => {});
  }, [moduleId, typeEndpoint, subtypeEndpoint]);

  const toggleSection = (key) =>
    setOpenSections((p) => ({ ...p, [key]: !p[key] }));

  // When Level1 saves, refresh moduleActive
  const handleSnack = useCallback((message, severity = "success") => {
    onSnack(message, severity);
    api.get(`/notification-settings/${moduleId}`)
      .then((r) => setModuleActive(Boolean(r.data.is_active ?? 1)))
      .catch(() => {});
  }, [moduleId, onSnack]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

      {/* ── Level 1: Module Controls ── */}
      <Paper elevation={0} sx={cardSx}>
        <SectionHeader
          icon={<NotificationsActiveIcon />}
          title="Module Controls"
          subtitle="Master switch and member auto-notification settings"
          open={openSections.l1}
          onToggle={() => toggleSection("l1")}
        />
        <Collapse in={openSections.l1}>
          <Level1Controls
            moduleId={moduleId}
            itemLabel={itemLabel}
            onSnack={handleSnack}
          />
        </Collapse>
      </Paper>

      {/* ── Level 2: Global Recipients ── */}
      <Paper elevation={0} sx={{ ...cardSx, opacity: moduleActive ? 1 : 0.6 }}>
        <SectionHeader
          icon={<GroupIcon />}
          title="Global Recipients"
          subtitle={`Staff emails notified for all ${itemLabel} events`}
          open={openSections.l2}
          onToggle={() => toggleSection("l2")}
          action={
            !moduleActive && (
              <Chip
                label="Module paused"
                size="small"
                color="error"
                variant="outlined"
                sx={{ mr: 1, fontSize: "0.72rem", fontWeight: 600 }}
              />
            )
          }
        />
        <Collapse in={openSections.l2}>
          <Divider />
          <Level2Recipients
            moduleId={moduleId}
            active={moduleActive}
            onSnack={onSnack}
          />
        </Collapse>
      </Paper>

      {/* ── Level 3: Type-Specific Rules ── */}
      <Paper elevation={0} sx={{ ...cardSx, opacity: moduleActive ? 1 : 0.6 }}>
        <SectionHeader
          icon={<CategoryIcon />}
          title="Type-Specific Rules"
          subtitle={`Extra routing by ${itemLabel} type or subtype`}
          open={openSections.l3}
          onToggle={() => toggleSection("l3")}
        />
        <Collapse in={openSections.l3}>
          <Divider />
          <Level3TypeRules
            moduleId={moduleId}
            active={moduleActive}
            types={types}
            subtypes={subtypeEndpoint ? subtypes : null}
            typeLabelKey={typeLabelKey}
            subtypeLabelKey={subtypeLabelKey}
            subtypeParentKey={subtypeParentKey}
            onSnack={onSnack}
          />
        </Collapse>
      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((p) => ({ ...p, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotificationModuleSettings;
